import {
  createHub,
  CreateHubOkResponse,
  listHub,
  ListHubOkResponse,
  retrieveHub,
  RetrieveHubOkResponse,
  partialUpdateHub,
  PartialUpdateHubOkResponse,
  deleteHub,
  duplicateHub,
  HubDuplicateRequestRequest
} from '@goldcast/api/backend';

import { FilterBy } from './types';
import { transformServerEventHubPages } from './utils';

import router from '@/router';

const sortByMap = {
  0: 'name',
  1: '-name',
  2: 'created_at',
  3: '-created_at'
};

const filterByMap = {
  [FilterBy.AllPages]: null,
  [FilterBy.OrganizationPages]: true,
  [FilterBy.WorkspacePages]: false
};

const actions = {
  async loadEventHubPages({ dispatch, commit, state, rootGetters }, { workspaceId = '' }) {
    const { searchTerm, currentPage, eventHubPagesPerPage, sortBy, filterBy } = state;
    const orgId: string = rootGetters['org/getCurrentOrgId'];

    commit('setIsLoading', true);

    return new Promise((resolve, reject) => {
      listHub({
        queryParams: {
          organization: orgId,
          offset: (currentPage - 1) * eventHubPagesPerPage,
          limit: eventHubPagesPerPage,
          ordering: sortByMap[sortBy] ?? '',
          name: searchTerm,
          workspace: workspaceId,
          workspace__isnull: filterByMap[filterBy]
        }
      })
        .then((eventHubPagesData: ListHubOkResponse) => {
          // @ts-ignore
          const eventHubPages = transformServerEventHubPages(eventHubPagesData.results);
          commit('setEventHubPages', eventHubPages);
          commit('setEventHubPagesCount', eventHubPagesData.count);
          resolve(eventHubPages);
        })
        .catch(error => {
          dispatch(
            'studioToast/triggerToast',
            { msg: 'Error Loading Pages, Please Try Again', mode: 'error' },
            { root: true }
          );
          reject(error);
        })
        .finally(() => {
          commit('setIsLoading', false);
        });
    });
  },

  async fetchAllEventHubs({ dispatch, rootGetters }) {
    try {
      const orgId: string = rootGetters['org/getCurrentOrgId'];
      const eventHubs = await listHub({
        queryParams: {
          organization: orgId
        }
      });
      return eventHubs;
    } catch (error) {
      dispatch('studioToast/triggerToast', { msg: 'Error Loading Pages!', mode: 'error' }, { root: true });
    }
  },

  async createEventHubPage({ commit, rootGetters }, params) {
    const orgId: string = rootGetters['org/getCurrentOrgId'];
    const payload = {
      ...params,
      organization: orgId
    };
    return new Promise((resolve, reject) => {
      createHub({
        body: payload
      })
        .then((newEventHubPage: CreateHubOkResponse) => {
          const eventHubPages = transformServerEventHubPages([newEventHubPage]);
          commit('setEventHubPages', eventHubPages);
          commit('setEventHubPagesCount', 1);
          resolve(newEventHubPage);
        })
        .catch(reject);
    });
  },

  async duplicateEventHubPage({ commit, rootGetters }, params: { req: HubDuplicateRequestRequest; pageId: string }) {
    const orgId: string = rootGetters['org/getCurrentOrgId'];
    const payload = {
      ...params.req,
      organization: orgId
    };
    return new Promise((resolve, reject) => {
      duplicateHub({
        body: payload,
        id: params.pageId
      })
        .then((newEventHubPage: CreateHubOkResponse) => {
          const eventHubPages = transformServerEventHubPages([newEventHubPage]);
          commit('setEventHubPages', eventHubPages);
          commit('setEventHubPagesCount', 1);
          resolve(newEventHubPage);
        })
        .catch(reject);
    });
  },

  async loadSingleEventHubPage({ dispatch, commit }, pageId: string) {
    commit('setIsLoading', true);

    return new Promise((resolve, reject) => {
      retrieveHub({
        id: pageId
      })
        .then((eventHubPage: RetrieveHubOkResponse) => {
          const eventHubPages = transformServerEventHubPages([eventHubPage]);
          commit('setEventHubPages', eventHubPages);
          commit('setEventHubPagesCount', 1);
          commit('setIsLoading', false);
          resolve(eventHubPage);
        })
        .catch(() => {
          dispatch(
            'studioToast/triggerToast',
            { msg: 'Error Loading Pages, Please Try Again', mode: 'error' },
            { root: true }
          );
          commit('setIsLoading', false);
          reject();
        });
    });
  },

  async updateEventHubPage(context, { pageId, eventHubPage, isBuilderUpdate = false }) {
    if (!isBuilderUpdate) {
      context.commit('setIsLoading', true);
    }

    try {
      const updatedEventHub: PartialUpdateHubOkResponse = await partialUpdateHub({
        id: pageId,
        body: eventHubPage
      });
      context.commit('setEventHubPage', updatedEventHub);
      if (!isBuilderUpdate) {
        const toastMessage = {
          msg: 'Event Hub updated successfully!',
          mode: 'success'
        };
        context.dispatch('studioToast/triggerToast', toastMessage, {
          root: true
        });
      }
      return updatedEventHub;
    } finally {
      context.commit('setIsLoading', false);
    }
  },

  async deleteEventHubPage(context, pageId: string) {
    try {
      await deleteHub({
        id: pageId
      });
      context.commit('removeEventHubPage', pageId);
      const toastMessage = {
        msg: 'Event Hub deleted successfully!',
        mode: 'success'
      };
      context.dispatch('studioToast/triggerToast', toastMessage, {
        root: true
      });
      router.push({ name: 'Pages' });
    } catch (error) {
      const toastMessage = {
        msg: 'Error: Failed to delete event hub!',
        mode: 'error'
      };
      context.dispatch('studioToast/triggerToast', toastMessage, {
        root: true
      });
    }
  }
};

export default actions;
